import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Stethoscope, ArrowRight, Layers } from 'lucide-react';

/**
 * Dual-audience operator strip for /services/:slug pages.
 * Pivots the SEO-rich service content from "we provide this service" to
 * "CaraLoom orchestrates this service line for the people who deliver it":
 *  • Hospital case managers + discharge planners
 *  • Home-health / hospice agencies + clinic operators
 *  • Independent clinicians staffing this line of work
 *  • Patients & families on the receiving end
 */
export function ServiceOperatorStrip({ serviceName }) {
  return (
    <section className="bg-cn-bone-50 border-y border-cn-border" data-testid="service-operator-strip">
      <div className="cn-container py-10">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="cn-eyebrow mb-2 flex items-center gap-2 text-cn-accent">
              <Layers className="w-4 h-4" /> Run this service line on CaraLoom
            </div>
            <h2 className="cn-display text-2xl sm:text-3xl text-cn-text mb-3">
              {serviceName} — the operating system underneath
            </h2>
            <p className="text-cn-text-muted leading-relaxed max-w-2xl">
              Hospital discharge planners, agency operators, and independent clinicians use
              CaraLoom to orchestrate every {serviceName.toLowerCase()} episode end-to-end —
              referrals, scheduling, clinical notes, RCM, payer contracts, and family
              communication on one HIPAA-ready stack.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3 justify-start lg:justify-end">
            <Link to="/pricing" className="cn-btn cn-btn-primary" data-testid="service-strip-provider-cta">
              <Building2 className="w-4 h-4" /> For agencies & hospitals
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="cn-btn cn-btn-ghost" data-testid="service-strip-clinician-cta">
              <Stethoscope className="w-4 h-4" /> For clinicians
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-7 text-sm">
          {[
            { suite: 'Referrals', desc: 'FHIR + faxed referral intake, AI triage, hospital→agency handoff in minutes.', href: '/suites/referrals' },
            { suite: 'Workforce', desc: 'Open-shift broadcast, credentialing, CEU library, paid-per-visit clinicians.', href: '/suites/workforce' },
            { suite: 'Revenue Cycle', desc: 'Pre-submission AI scrub, 837P/835, denial prediction, family auto-statements.', href: '/suites/revenue-cycle' },
            { suite: 'Compliance', desc: 'SOC 2 evidence, BAA chain, audit-trail export, payer-VBC contracts.', href: '/suites/compliance' },
          ].map((s) => (
            <Link
              key={s.suite}
              to={s.href}
              className="cn-card p-4 rounded-2xl hover:shadow-cn-floating transition-shadow"
              data-testid={`service-strip-suite-${s.suite.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="font-semibold text-cn-text mb-1">{s.suite}</div>
              <p className="text-xs text-cn-text-muted leading-relaxed">{s.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
